import { useQueries } from '@tanstack/react-query';
import { Building2, Calendar, Star, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApartments } from '../hooks/useApartments';
import { bookingQueryOptions } from '../hooks/useBookings';
import { useReviews } from '../hooks/useReviews';
import { useOccupancy } from '../hooks/useOccupancy';
import { OccupancyDirection } from '../types/occupancy';
import { BookingStatusBadge } from '../components/BookingStatusBadge';
import { ReviewStatusBadge } from '../components/ReviewStatusBadge';
import { formatDate } from '../utils/formatDate';

const Dashboard = () => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const {
    data: apartments = [],
    isLoading: aptsLoading,
    isError: aptsError,
  } = useApartments();

  const bookingQueries = useQueries({
    queries: apartments.map(apt => bookingQueryOptions(apt.id)),
  });

  const bookingsLoading  = bookingQueries.some(q => q.isLoading);
  const someBookingsError = bookingQueries.some(q => q.isError);
  const allBookingsError  = bookingQueries.length > 0 && bookingQueries.every(q => q.isError);
  const bookingsPartial   = someBookingsError && !allBookingsError;
  const allBookings       = bookingQueries.flatMap(q => q.data ?? []);

  // Recent reviews — last 3, no status filter
  const {
    data: reviewsData,
    isLoading: reviewsLoading,
    isError: reviewsError,
  } = useReviews({}, 0, 3);

  // Occupancy rate — aggregate across all owner apartments
  const {
    data: occupancyData,
    isLoading: occupancyLoading,
    isError: occupancyError,
  } = useOccupancy();

  // ── Derived values ─────────────────────────────────────────────────────────
  const confirmedCount = allBookings.filter(b => b.status === 'CONFIRMED').length;

  const recentBookings = [...allBookings]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, 5);

  // ── Stat card helpers ──────────────────────────────────────────────────────
  const totalApartmentsValue: string | null =
    aptsError    ? '—'
    : aptsLoading ? null
    : String(apartments.length);

  const activeBookingsValue: string | null =
    aptsError || allBookingsError ? '—'
    : aptsLoading || bookingsLoading ? null
    : String(confirmedCount);

  const occupancyRateValue: string | null =
    occupancyError ? '—'
    : occupancyLoading ? null
    : `${occupancyData?.currentMonth.occupancyRate ?? 0}%`;

  // ── Occupancy trend helpers ────────────────────────────────────────────────
  const TREND_CONFIG: Record<OccupancyDirection, { icon: typeof TrendingUp; colorClass: string }> = {
    INCREASE: { icon: TrendingUp,   colorClass: 'text-green-500' },
    DECREASE: { icon: TrendingDown, colorClass: 'text-red-500'   },
    STABLE:   { icon: Minus,        colorClass: 'text-gray-400'  },
  };

  const momChange = occupancyData?.monthOverMonthChange ?? null;

  const stats = [
    { name: t('pages:totalApartments'), value: totalApartmentsValue, icon: Building2 },
    { name: t('pages:activeBookings'),  value: activeBookingsValue,  icon: Calendar  },
    { name: t('pages:averageRating'),   value: '4.8',                icon: Star,     change: '+0.2', changeType: 'positive' },
    { name: t('pages:occupancyRate'),   value: occupancyRateValue,   icon: TrendingUp },
  ];

  // ── Recent Bookings panel content ──────────────────────────────────────────
  const renderRecentBookings = () => {
    if (aptsError) {
      return (
        <p className="text-secondary-500 text-center py-8">{t('errors:failedToLoadBookingsDashboard')}</p>
      );
    }

    if (aptsLoading || bookingsLoading) {
      return (
        <div className="flex justify-center py-8">
          <span className="inline-block w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (recentBookings.length === 0) {
      return (
        <p className="text-secondary-500 text-center py-8">{t('pages:noRecentBookings')}</p>
      );
    }

    return (
      <>
        <div className="space-y-2">
          {recentBookings.map(booking => {
            const aptName =
              apartments.find(a => a.id === booking.apartmentId)?.name ?? booking.apartmentId;
            return (
              <div
                key={booking.bookingId}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{aptName}</p>
                  <p className="text-xs text-secondary-500 truncate">{booking.guestInfo.fullName}</p>
                </div>
                <div className="ml-4 text-right shrink-0">
                  <p className="text-xs text-secondary-500 whitespace-nowrap">
                    {formatDate(booking.checkInDate)} – {formatDate(booking.checkOutDate)}
                  </p>
                  <div className="mt-1">
                    <BookingStatusBadge status={booking.status} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {bookingsPartial && (
          <p className="text-xs text-secondary-500 mt-2">
            {t('errors:partialBookingsError')}
          </p>
        )}
      </>
    );
  };

  // ── Recent Reviews panel content ───────────────────────────────────────────
  const recentReviews = reviewsData?.content ?? [];

  const renderRecentReviews = () => {
    if (reviewsError) {
      return (
        <p className="text-secondary-500 text-center py-8">{t('errors:failedToLoadReviews')}</p>
      );
    }

    if (reviewsLoading) {
      return (
        <div className="flex justify-center py-8">
          <span className="inline-block w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (recentReviews.length === 0) {
      return (
        <p className="text-secondary-500 text-center py-8">{t('pages:noRecentReviews')}</p>
      );
    }

    return (
      <div className="space-y-2">
        {recentReviews.map(review => {
          const aptName =
            apartments.find(a => a.id === review.apartmentId)?.name ?? review.apartmentId;
          return (
            <div
              key={review.id}
              className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{aptName}</p>
                <p className="text-xs text-secondary-500 truncate">{review.customerName}</p>
              </div>
              <div className="ml-4 text-right shrink-0">
                <div className="flex items-center gap-1 justify-end">
                  <Star size={12} className="text-amber-400 fill-amber-400" />
                  <span className="text-xs text-secondary-500">{review.rating}</span>
                </div>
                <div className="mt-1">
                  <ReviewStatusBadge status={review.status} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('pages:dashboardHeading')}</h1>
        <p className="text-secondary-500 mt-2">{t('pages:dashboardSubHeading')}</p>
      </div>

      {/* ── Stat cards ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-primary-50 p-3 rounded-lg">
                  <stat.icon className="text-primary-500" size={24} />
                </div>
              </div>
              {'change' in stat && stat.change && (
                <span className={`text-sm font-semibold ${
                  stat.changeType === 'positive' ? 'text-accent-500' : 'text-red-500'
                }`}>
                  {stat.change}
                </span>
              )}
              {stat.name === t('pages:occupancyRate') && momChange && (() => {
                const { icon: TrendIcon, colorClass } = TREND_CONFIG[momChange.direction];
                const sign = momChange.changePercentagePoints >= 0 ? '+' : '';
                return (
                  <span
                    className={`flex items-center gap-1 text-sm font-semibold ${colorClass}`}
                    data-testid="occupancy-trend-icon"
                  >
                    <TrendIcon size={20} />
                    {`${sign}${momChange.changePercentagePoints.toFixed(1)} pp`}
                  </span>
                );
              })()}
            </div>
            <div className="mt-4">
              {stat.value === null ? (
                <div
                  className="h-8 w-16 bg-gray-200 rounded animate-pulse"
                  aria-label={t('common:loading')}
                />
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              )}
              <p className="text-sm text-secondary-500 mt-1">{stat.name}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Content panels ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-xl font-bold mb-4">{t('pages:recentBookings')}</h2>
          <div className="space-y-3">
            {renderRecentBookings()}
          </div>
        </div>

        <div className="card">
          <h2 className="text-xl font-bold mb-4">{t('pages:recentReviews')}</h2>
          <div className="space-y-3">
            {renderRecentReviews()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
